import { useState, useEffect, useRef } from 'react';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import VideoSlider from '../components/VideoSlider';
import { services, clients, testimonials, projects, awards, teamMembers, processSteps, faqs } from '../data';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, Award, Building, Users, Clock, ChevronRight, Check, ArrowDown, ExternalLink, Play, Zap, Shield, Globe, Wrench, ChevronLeft, X } from 'lucide-react';
import StatCounter from '../components/StatCounter';
import ProjectCard from '../components/ProjectCard';
import NewsCard from '../components/NewsCard';
import AccordionItem from '../components/AccordionItem';
import ParallaxSection from '../components/ParallaxSection';
import { useNavigate } from 'react-router-dom';

function Home() {
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const modalRef = useRef(null);

  const { scrollYProgress } = useScroll();
  const opacity = useTransform(scrollYProgress, [0, 0.1], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.1], [1, 0.95]);

  // const stats = [
  //   { value: 25, label: 'Years Experience', icon: Clock },
  //   { value: 500, label: 'Projects Completed', icon: Building },
  //   { value: 150, label: 'Happy Clients', icon: Users },
  //   { value: 15, label: 'Industry Awards', icon: Award },
  // ];

  const coreValues = [
    { 
      icon: <Zap className="w-8 h-8 text-sky-blue" />, 
      title: 'Innovation', 
      description: 'Embracing cutting-edge technologies and methodologies to push the boundaries of structural engineering.'
    },
    { 
      icon: <Shield className="w-8 h-8 text-sky-blue" />, 
      title: 'Integrity', 
      description: 'Upholding the highest standards of professional ethics and responsibility in all our work.'
    },
    { 
      icon: <Globe className="w-8 h-8 text-sky-blue" />, 
      title: 'Sustainability', 
      description: 'Designing with environmental consciousness to create structures that stand the test of time.'
    },
    { 
      icon: <Wrench className="w-8 h-8 text-sky-blue" />, 
      title: 'Excellence', 
      description: 'Striving for perfection in every detail, from initial concept to final implementation.'
    },
  ];

  const slideContent = [
    {
      heading: <>A clear view of the <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-300">glazing</span> industry</>,
      description: 'Innovative structural engineering solutions that balance safety, aesthetics, and sustainability for projects of any scale.'
    },
    {
      heading: <>A clear view of the <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-300">glazing</span> industry</>,
      description: 'Delivering precise and reliable engineering designs to turn visionary architecture into reality.'
    },
    {
      heading: <>A clear view of the <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-500 to-cyan-300">glazing</span> industry</>,
      description: 'Transforming ideas into enduring structures with cutting-edge technology and expertise.'
    }
  ];

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    pauseOnHover: false,
    appendDots: dots => (
      <div style={{ position: 'absolute', bottom: '50px', width: '100%', textAlign: 'center' }}>
        <ul style={{ margin: '0px', padding: '0', display: 'inline-flex', gap: '12px' }}>
          {dots}
        </ul>
      </div>
    ),
    customPaging: i => (
      <div
        className="hero-slick-dot"
        style={{
          width: '12px',
          height: '12px',
          margin: '0 6px',
          borderRadius: '50%',
          background: 'rgba(255, 255, 255, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
        }}
      >
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'transparent',
            transition: 'all 0.3s ease',
          }}
        />
      </div>
    ),
  };

  const testimonialSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    arrows: false,
    pauseOnHover: true,
    appendDots: dots => (
      <div style={{ position: 'absolute', bottom: '-40px', width: '100%', textAlign: 'center' }}>
        <ul style={{ margin: '0px', padding: '0', display: 'inline-flex', gap: '12px' }}>
          {dots}
        </ul>
      </div>
    ),
    customPaging: i => (
      <div
        className="slick-dot"
        style={{
          width: '12px',
          height: '12px',
          margin: '0 6px',
          borderRadius: '50%',
          background: 'rgba(0, 0, 0, 0.3)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          cursor: 'pointer',
          transition: 'all 0.3s ease',
        }}
      >
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: 'transparent',
            transition: 'all 0.3s ease',
          }}
        />
      </div>
    ),
  };

  const openProjectModal = (project, imageIndex = 0) => {
    setSelectedProject(project);
    setCurrentImageIndex(imageIndex);
  };

  const closeProjectModal = () => {
    setSelectedProject(null);
    setCurrentImageIndex(0);
  };

  const nextImage = () => {
    if (selectedProject && currentImageIndex < selectedProject.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (selectedProject && currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  // Handle click outside to close modal
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        closeProjectModal();
      }
    };

    if (selectedProject) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectedProject]);

  const navigate = useNavigate();

  return (
    <div className="bg-gray-50 overflow-hidden">
      <style>
        {`
          .slick-dots li.slick-active .slick-dot div {
            background: #000000 !important;
          }
          .slick-dots li .slick-dot {
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .slick-dots li.slick-active .hero-slick-dot div {
            background: #000000 !important;
          }
          .slick-dots li .hero-slick-dot {
            display: flex;
            justify-content: center;
            align-items: center;
          }
        `}
      </style>
<motion.div 
  style={{ opacity, scale }}
  className="relative h-screen mt-10 pt-20 mb-10"
>
<Slider {...sliderSettings}>
  {slideContent.map((content, index) => (
    <div key={index} className="relative h-screen">
      <div className="absolute inset-0 z-0">
        {index === 0 ? (
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover"
            poster="/assets/images/hero-fallback.jpg"
          >
            <source 
              src="/assets/videos/hero-background1.mp4" 
              type="video/mp4" 
            />
            Your browser does not support the video tag.
          </video>
        ) : (
          <img
            src={index === 1 ? "/assets/images/hero-fallback.jpg" : "/assets/images/hero-fallback2e.jpg"}
            alt={`Slide ${index + 1} background`}
            className="w-full h-full object-cover"
          
          />
        )}
        {/* Increased overlay opacity for better text visibility */}
        <div className="absolute inset-0 bg-dark-blue/20 z-10"></div>
      </div>
      
      <div className="absolute inset-0 flex items-center justify-center z-20">
        <div className="container mx-auto px-4 flex items-center justify-center h-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center text-white max-w-4xl"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight">
              {content.heading}
            </h1>
            <p className="mt-6 text-lg md:text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto leading-relaxed">
              {content.description}
            </p>
            <div className="mt-10">
              <a
                href="/services"
                className="bg-gradient-to-r from-sky-500 to-sky-500 text-white py-4 px-8 rounded-lg hover:bg-sky-blue/100 transition duration-300 font-medium text-lg inline-flex items-center"
              >
                Explore Our Services <ArrowRight className="ml-2 h-5 w-5" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  ))}
</Slider>
</motion.div>
<section className="container mx-auto px-4 py-20">
<motion.div
  id="intro"
  initial={{ opacity: 0 }}
  whileInView={{ opacity: 1 }}
  transition={{ duration: 0.7 }}
  viewport={{ once: true }}
  className="grid md:grid-cols-2 gap-12 items-center"
>
  {/* Text Column */}
  <div className="text-left">
    <span className="text-dark-blue font-semibold uppercase tracking-wider">Engineering Excellence</span>
    <h2 className="mt-3 text-4xl md:text-5xl font-bold text-dark-blue">
      Welcome to <span className="text-dark-blue">The Facade Connection</span>
    </h2>
    <div className="mt-6 text-lg md:text-xl text-gray-600 leading-relaxed space-y-4 text-justify">
      <p>
        At The Facade Connection, we blend over 20 years of industry experience with
        innovative, high-performance engineering solutions. Founded in 2020, our team has been
        shaping the glazing industry since 2015, delivering results that exceed expectations and
        ensure project success.
      </p>
      <p>
        With a deep understanding of client needs, we tailor our services to ensure smooth
        execution from start to finish. Licensed across more than 10 states, including Florida,
        New York, New Jersey, Texas, and more, we proudly serve the southeast and central
        U.S., helping clients bring their vision to life with confidence.
      </p>
    </div>
    <div className="mt-10">
      <a
        href="/about"
        className="bg-dark-blue text-white py-3 px-8 rounded-lg bg-gradient-to-r from-sky-blue to-dark-blue hover:from-dark-blue hover:to-sky-blue rounded-lg py-2 px-4 transition shadow-md font-medium inline-flex items-center"
      >
        Learn More <ChevronRight className="ml-2 h-4 w-4" />
      </a>
    </div>
  </div>

  {/* Image Column */}
  <div className="relative">
    <img
      src="/assets/images/ESW/esw-westwharf-1.jpg"
      alt="Facade engineering and glazing work"
      className="w-full h-96 rounded-lg shadow-lg object-cover"
    />
  </div>
</motion.div>
</section>

<section className='bg-gradient-to-r from-indigo-900 to-teal-600 '>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          viewport={{ once: true }}
          className="container mx-auto px-4 mt-24 pt-20 pb-20"
        >
          <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-12">
            <div className=" text-center md:text-left mb-6 md:mb-0">
              <span className="text-white font-semibold uppercase tracking-wider">What we offer</span>
              <h2 className="text-3xl md:text-4xl font-bold text-white mt-2">Our Services</h2>
              <p className="mt-3 text-lg text-white max-w-2xl">
                Comprehensive engineering solutions tailored to your project needs
              </p>
            </div>
            <a href="/services" className="text-white font-medium flex items-center hover:text-indigo-900 transition">
              View All Services <ChevronRight className="h-4 w-4 ml-1" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services && services.length > 0 ? (
              services.map((service, index) => (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition duration-300 group flex flex-col"
                >
                  <div className="h-48 w-full overflow-hidden rounded-t-xl">
                    <img
                      src={service.bgImage || '/assets/images/default-service-bg.jpg'}
                      alt={service.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/images/default-service-bg.jpg';
                      }}
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-semibold text-indigo-900 mb-2">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed flex-grow">{service.previewDescription}</p>
                    <button
                      onClick={() => navigate('/services', { state: { serviceId: service.id } })}
                      className="mt-4 inline-flex items-center justify-center text-white font-medium bg-gradient-to-r from-sky-blue to-dark-blue hover:from-dark-blue hover:to-sky-blue rounded-lg py-2 px-4 transition shadow-md"
                    >
                      Learn More <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </motion.div>
              ))
            ) : (
              <p className="text-center text-gray-600 col-span-3">No services available.</p>
            )}
          </div>
        </motion.div>
</section>

      <section className="container mx-auto px-4 py-20">

        
        {/* Featured Projects Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-12">
            <div className="text-center md:text-left mb-6 md:mb-0">
              <span className="text-sky-blue font-semibold uppercase tracking-wider">Our Work</span>
              <h2 className="text-3xl md:text-4xl font-bold text-dark-blue mt-2">Featured Projects</h2>
              <p className="mt-3 text-lg text-gray-600 max-w-2xl">
                Explore some of our standout projects showcasing innovation and excellence.
              </p>
            </div>
            <a
              href="/projects"
              className="text-sky-600 font-medium flex items-center hover:text-indigo-900 transition"
            >
              See All Projects <ChevronRight className="h-4 w-4 ml-1" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {projects && projects.length > 0 ? (
              projects.slice(0, 6).map((project, index) => (
                <motion.div
                  key={project.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 * index }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-md hover:shadow-lg transition duration-300 group flex flex-col"
                >
                  <div className="h-48 w-full overflow-hidden rounded-t-xl cursor-pointer">
                    <img
                      src={project.images[0].src}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onClick={() => openProjectModal(project, 0)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/images/default-project-bg.jpg';
                      }}
                    />
                  </div>
                  <div className="p-6 flex flex-col flex-grow">
                    <h3 className="text-xl font-semibold text-indigo-900 mb-2">{project.title}</h3>
                    <p className="text-gray-600 leading-relaxed flex-grow line-clamp-3">{project.description}</p>
                    <button
                      onClick={() => openProjectModal(project, 0)}
                      className="mt-4 inline-flex items-center justify-center text-white font-medium bg-gradient-to-r from-sky-blue to-dark-blue hover:from-dark-blue hover:to-sky-blue rounded-lg py-2 px-4 transition shadow-md rounded-lg py-2 px-4"
                    >
                      Learn More <ChevronRight className="h-4 w-4 ml-1" />
                    </button>
                  </div>
                </motion.div>
              ))
            ) : (
              <p className="text-center text-gray-600 col-span-3">No projects available.</p>
            )}
          </div>
        </motion.div>
        

<motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          {/* Full-width background section */}
          <div className="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-gradient-to-r from-orange-50 via-red-50 to-pink-50 py-16">
            <div className="container mx-auto px-6">
              <div className="text-center mb-12">
                <span className="text-sky-blue font-semibold uppercase tracking-wider">Trusted by</span>
                <h2 className="text-3xl md:text-4xl font-bold text-dark-blue mt-2">Our Clients</h2>
                <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
                  We're proud to work with industry leaders across various sectors
                </p>
              </div>
              
              <div className="py-10 px-6 bg-white rounded-xl shadow-lg">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-8 items-center">
                  {clients && clients.length > 0 ? (
                    clients.map((client, index) => (
                      <motion.img
                        key={index}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.5, delay: index * 0.05 }}
                        viewport={{ once: true }}
                        src={client}
                        alt={`Client ${index + 1}`}
                        className="h-16 object-contain mx-auto filter grayscale hover:grayscale-0 transition duration-300"
                      />
                    ))
                  ) : (
                    <p className="text-center text-gray-600 col-span-6">No clients available.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
<motion.div
  initial={{ opacity: 0, y: 30 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7, delay: 0.6 }}
  viewport={{ once: true }}
  className="mt-24 py-16 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50"
>
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <div className="text-center mb-16">
      <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4 uppercase tracking-wider">
        <svg className="w-4 h-4 fill-current" viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
        Client feedback
      </div>
      <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
        What Our <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600">Clients Say</span>
      </h2>
      <p className="text-lg text-gray-600 max-w-2xl mx-auto">
        Discover why businesses trust us to deliver exceptional results
      </p>
    </div>
    
    <div className="relative min-h-[500px]"> 
      {testimonials && testimonials.length > 0 ? (
        <Slider {...testimonialSliderSettings}>
          {testimonials.map((testimonial, index) => (
            <div key={index} className="px-4">
              <div className="bg-white/80 backdrop-blur-sm p-8 md:p-12 rounded-2xl shadow-xl border border-white/50 max-w-5xl mx-auto hover:shadow-2xl transition-all duration-300">
                <div className="flex flex-col lg:flex-row gap-8 items-center">
                  {/* Enhanced Avatar/Logo Section */}
                  <div className="lg:w-1/3 flex-shrink-0">
                    <div className="relative group">
                      <div className="w-40 h-40 md:w-48 md:h-48 rounded-2xl bg-gradient-to-br from-blue-500 via-purple-500 to-pink-500 p-1 mx-auto">
                        <div className="w-full h-full rounded-2xl bg-white flex items-center justify-center overflow-hidden">
                          {testimonial.logo ? (
                            <img
                              src={testimonial.logo}
                              alt={`${testimonial.name} logo`}
                              className="w-3/4 h-3/4 object-contain filter drop-shadow-lg"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center">
                              <span className="text-4xl md:text-5xl font-bold text-white drop-shadow-lg">
                                {testimonial.name.split(' ').map(word => word.charAt(0)).join('').slice(0, 2)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Enhanced Rating Badge */}
                      <div className="absolute -bottom-3 -right-3 bg-white rounded-full p-3 shadow-lg border-4 border-yellow-100">
                        <div className="flex items-center gap-1">
                          {[...Array(5)].map((_, i) => (
                            <svg key={i} className="w-4 h-4 text-yellow-400 drop-shadow-sm" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118l-2.8-2.034c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                            </svg>
                          ))}
                        </div>
                        <div className="text-xs font-semibold text-gray-700 mt-1 text-center">5.0</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Enhanced Content Section */}
                  <div className="lg:w-2/3 text-center lg:text-left">
                    {/* Quote Icon */}
                    <div className="mb-6">
                      <svg className="h-12 w-12 text-blue-200 mx-auto lg:mx-0" fill="currentColor" viewBox="0 0 32 32">
                        <path d="M10 8c-3.3 0-6 2.7-6 6v10h10V14h-6c0-3.3 2.7-6 6-6V8zm18 0c-3.3 0-6 2.7-6 6v10h10V14h-6c0-3.3 2.7-6 6-6V8z" />
                      </svg>
                    </div>
                    
                    {/* Quote Text */}
                    <div className="mb-8">
                      <p id={`testimonial-text-${index}`} className="text-lg md:text-xl text-gray-700 leading-relaxed italic font-medium">
                        {testimonial.quote.split(' ').slice(0, Math.ceil(testimonial.quote.split(' ').length / 2)).join(' ')}
                        {testimonial.quote.split(' ').length > 20 && !document.getElementById(`testimonial-text-${index}`)?.classList.contains('expanded') && '...'}
                      </p>
                      {testimonial.quote.split(' ').length > 20 && (
                        <button
                          onClick={() => {
                            const textElement = document.getElementById(`testimonial-text-${index}`);
                            if (textElement) {
                              if (textElement.classList.contains('expanded')) {
                                textElement.innerText = testimonial.quote.split(' ').slice(0, Math.ceil(testimonial.quote.split(' ').length / 2)).join(' ') + '...';
                                textElement.classList.remove('expanded');
                                textElement.nextElementSibling.innerText = 'Read More';
                              } else {
                                textElement.innerText = testimonial.quote;
                                textElement.classList.add('expanded');
                                textElement.nextElementSibling.innerText = 'Read Less';
                              }
                            }
                          }}
                          className="inline-flex items-center mt-4 px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold hover:bg-blue-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                          Read More
                          <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>
                      )}
                    </div>
                    
                    {/* Author Info */}
                    <div className="border-t border-gray-100 pt-6">
                      <h4 className="font-bold text-gray-900 text-xl mb-2">{testimonial.name}</h4>
                      <p className="text-blue-600 font-medium">
                        {testimonial.position}
                        {testimonial.position && testimonial.company ? " • " : ""}
                        <span className="text-gray-600">{testimonial.company}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </Slider> 
      ) : (
        <div className="text-center py-20">
          <div className="w-24 h-24 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
            <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
          </div>
          <p className="text-gray-500 text-lg">No testimonials available at the moment.</p>
        </div>
      )}
    </div>
  </div>
</motion.div>

        {/* <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.7 }}
          viewport={{ once: true }}
          className="mt-24"
        >
          <div className="text-center mb-12">
            <span className="text-sky-blue font-semibold uppercase tracking-wider">Common questions</span>
            <h2 className="text-3xl md:text-4xl font-bold text-dark-blue mt-2">Frequently Asked Questions</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              Find answers to common questions about our services and process
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 max-w-5xl mx-auto">
            {faqs && faqs.length > 0 ? (
              faqs.slice(0, 6).map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  question={faq.question} 
                  answer={faq.answer} 
                  delay={index * 0.1} 
                />
              ))
            ) : (
              <p className="text-center text-gray-600 col-span-2">No FAQs available.</p>
            )}
          </div>
        </motion.div> */}


      </section>
<motion.div
  initial={{ opacity: 0, y: 30 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ duration: 0.7, delay: 0.9 }}
  viewport={{ once: true }}
  className="mt-24 w-full"
>
  <div className="bg-gradient-to-br from-dark-blue via-slate-800 to-sky-blue">
    <div className="py-16 px-8 md:py-20 md:px-16">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-3xl md:text-5xl font-bold text-white leading-tight">
          Ready to Transform Your <span className="text-sky-blue">Engineering Vision</span> into Reality?
        </h2>
        <p className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
          Let's collaborate to bring your structural engineering projects to life. Our team of experts is ready to help you achieve your goals with innovative solutions.
        </p>
        
        <div className="mt-10 flex flex-wrap justify-center gap-4">
          <a
            href="/contact"
            className="bg-white text-dark-blue py-4 px-8 rounded-lg hover:bg-gray-100 transition duration-300 font-medium text-lg flex items-center"
          >
            Contact Us Today <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
        
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
          <div className="flex flex-col items-center">
            <div className="bg-white/20 w-14 h-14 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
            </div>
            <p className="text-white font-medium">+1 (305) 528-1054</p>
          </div>
          
          <div className="flex flex-col items-center">
            <div className="bg-white/20 w-14 h-14 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2h14z" />
              </svg>
            </div>
            <p className="text-white font-medium">info@facadeconnection.com</p>
          </div>
          
          <div className="flex flex-col items-center">
            <div className="bg-white/20 w-14 h-14 rounded-full flex items-center justify-center mb-3">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <p className="text-white font-medium">18322 SW 135th Ave, Miami, FL 33177</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</motion.div> 
      {isVideoModalOpen && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl overflow-hidden shadow-2xl max-w-4xl w-full">
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-dark-blue">Structural Solutions Showreel</h3>
              <button 
                onClick={() => setIsVideoModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="aspect-video bg-gray-900">
              <iframe 
                width="100%" 
                height="100%" 
                src="https://video.wixstatic.com/video/11062b_c291d21ce5a1414d8ae0e554a5b89b5d/1080p/mp4/file.mp4" 
                title="Structural Solutions Showreel" 
                frameBorder="0" 
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
      )}

      {selectedProject && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div 
            ref={modalRef} 
            className="bg-white rounded-xl shadow-2xl max-w-4xl w-full my-8"
          >
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-dark-blue">{selectedProject.title}</h3>
              <button 
                onClick={closeProjectModal}
                className="text-gray-500 hover:text-gray-700 bg-gray-100 p-2 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 flex flex-col gap-6">
              <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                <img
                  src={selectedProject.images[currentImageIndex]?.src || '/assets/images/default-project-bg.jpg'}
                  alt={`${selectedProject.title} ${currentImageIndex + 1}`}
                  className="w-full h-[40vh] md:h-[50vh] object-contain"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/assets/images/default-project-bg.jpg';
                  }}
                />
                {selectedProject.images.length > 1 && (
                  <>
                    <button
                      onClick={prevImage}
                      disabled={currentImageIndex === 0}
                      className={`absolute top-1/2 left-4 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md ${
                        currentImageIndex === 0 ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100'
                      }`}
                    >
                      <ChevronLeft className="h-6 w-6 text-gray-800" />
                    </button>
                    <button
                      onClick={nextImage}
                      disabled={currentImageIndex === selectedProject.images.length - 1}
                      className={`absolute top-1/2 right-4 transform -translate-y-1/2 bg-white p-2 rounded-full shadow-md ${
                        currentImageIndex === selectedProject.images.length - 1
                          ? 'opacity-50 cursor-not-allowed'
                          : 'hover:bg-gray-100'
                      }`}
                    >
                      <ChevronRight className="h-6 w-6 text-gray-800" />
                    </button>
                  </>
                )}
              </div>
              {selectedProject.images.length > 1 && (
                <div className="flex overflow-x-auto gap-2 pb-2">
                  {selectedProject.images.map((image, index) => (
                    <img
                      key={index}
                      src={image.src}
                      alt={`${selectedProject.title} ${index + 1}`}
                      className={`h-16 w-24 object-cover rounded-md cursor-pointer transition-all ${
                        index === currentImageIndex ? 'border-2 border-sky-blue' : 'opacity-70 hover:opacity-100'
                      }`}
                      onClick={() => setCurrentImageIndex(index)}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = '/assets/images/default-project-bg.jpg';
                      }}
                    />
                  ))}
                </div>
              )}
              <div>
                <h4 className="text-xl font-semibold text-dark-blue mb-2">{selectedProject.title}</h4>
                <p className="text-gray-600 leading-relaxed mb-4">{selectedProject.description}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-sm font-semibold text-sky-blue uppercase">Client</h5>
                    <p className="text-gray-600">{selectedProject.client || 'N/A'}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-sky-blue uppercase">Location</h5>
                    <p className="text-gray-600">{selectedProject.location || 'N/A'}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-sky-blue uppercase">Year</h5>
                    <p className="text-gray-600">{selectedProject.year || 'N/A'}</p>
                  </div>
                  <div>
                    <h5 className="text-sm font-semibold text-sky-blue uppercase">Services</h5>
                    <p className="text-gray-600">{selectedProject.services?.join(', ') || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Home;